
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { get, put, post, del } from '@/services/http';
import { Container, Draggable } from 'vue-smooth-dnd';
import { EventBus } from '@/eventBus';

interface UnifiedColumn {
  id: string;
  label: string;
  type: 'required' | 'masterdata' | 'custom' | 'metafield';
  enabled: boolean;
  order: number;
  category?: string;
  // Metafield-specific
  metafieldKey?: string;
  namespace?: string;
}

// Metafield definition from API
interface MetafieldDefinition {
  key: string;           // e.g., "custom.supplier_code"
  namespace: string;     // e.g., "custom"
  name: string;          // e.g., "supplier_code"
  displayName: string;   // e.g., "Supplier Code"
  type: string;          // e.g., "single_line_text_field"
}

// Metafield column config (stored in UserConfigs)
interface MetafieldColumnConfig {
  enabled: boolean;
  order: number;
  label: string;
  namespace: string;
  name: string;
}

interface ColumnConfig {
  enabled: boolean;
  order: number;
}

interface CustomColumnData {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}

// Required columns - always shown, but can be reordered
const REQUIRED_COLUMNS = [
  { id: 'req_image', label: 'Image' },
  { id: 'req_product_details', label: 'Product Name' },
  { id: 'req_variant', label: 'Variant' },
  { id: 'req_sku', label: 'SKU' },
  { id: 'req_quantity', label: 'Quantity' },
  { id: 'req_unit_price', label: 'Unit Price' },
  { id: 'req_total', label: 'Total' }
];

// MasterData columns - optional
// Note: received_quantity, returned_quantity, units_left are from PO items (not Shopify API)
const MASTERDATA_COLUMNS = [
  // PO Line Item Fields (from PO line items, not MasterData API)
  { id: 'note', label: 'Note', category: 'PO Line Item' },
  { id: 'received_quantity', label: 'Received Quantity', category: 'PO Line Item' },
  { id: 'returned_quantity', label: 'Returned Quantity', category: 'PO Line Item' },
  { id: 'units_left', label: 'Units Left', category: 'PO Line Item' },
  { id: 'tax_type', label: 'Tax Type', category: 'PO Line Item' },
  { id: 'tax_rate', label: 'Tax Rate (%)', category: 'PO Line Item' },
  { id: 'tax_amount', label: 'Tax Amount', category: 'PO Line Item' },
  { id: 'landed_cost', label: 'Landed Cost', category: 'PO Line Item' },
  { id: 'landed_cost_shipping_per_unit', label: 'Shipping / Unit', category: 'PO Line Item' },
  { id: 'landed_cost_adjustment_per_unit', label: 'Adjustment / Unit', category: 'PO Line Item' },
  { id: 'margin_amount', label: 'Margin', category: 'PO Line Item' },
  { id: 'margin_percent', label: 'Margin %', category: 'PO Line Item' },
  { id: 'landed_margin_amount', label: 'Landed Margin', category: 'PO Line Item' },
  { id: 'landed_margin_percent', label: 'Landed Margin %', category: 'PO Line Item' },

  // Basic Information
  { id: 'variant_id', label: 'Variant ID', category: 'Basic' },
  { id: 'product_id', label: 'Product ID', category: 'Basic' },
  { id: 'item_description', label: 'Item Description', category: 'Basic' },
  { id: 'product_barcode', label: 'Product Barcode', category: 'Basic' },
  { id: 'vendor_name', label: 'Vendor Name', category: 'Basic' },
  { id: 'product_type', label: 'Product Type', category: 'Basic' },
  { id: 'product_status', label: 'Product Status', category: 'Basic' },

  // Pricing
  { id: 'unit_cost', label: 'Unit Cost', category: 'Pricing' },
  { id: 'selling_price', label: 'Selling Price', category: 'Pricing' },
  { id: 'retail_value', label: 'Retail Value', category: 'Pricing' },
  { id: 'actual_inventory_cost', label: 'Actual Inventory Cost', category: 'Pricing' },

  // Quantities
  { id: 'actual_inventory_quantity', label: 'Actual Inventory Quantity', category: 'Quantities' },
  { id: 'committed_quantity', label: 'Committed Quantity', category: 'Quantities' },
  { id: 'incoming_quantity', label: 'Incoming Quantity', category: 'Quantities' },
  { id: 'available_quantity', label: 'Available Quantity', category: 'Quantities' },

  // Product Attributes
  { id: 'total_weight', label: 'Total Weight', category: 'Attributes' },
  { id: 'weight_unit', label: 'Weight Unit', category: 'Attributes' },
  { id: 'hs_code', label: 'HS Code', category: 'Attributes' },
  { id: 'country_of_origin', label: 'Country of Origin', category: 'Attributes' },
  { id: 'inventory_tracking', label: 'Inventory Tracking', category: 'Attributes' },
  { id: 'continue_selling_out_stock', label: 'Continue Selling Out of Stock', category: 'Attributes' },

  // Metadata
  { id: 'product_tags', label: 'Product Tags', category: 'Metadata' },
  { id: 'product_collections', label: 'Product Collections', category: 'Metadata' },
  { id: 'product_published_date', label: 'Product Published Date', category: 'Metadata' },
];

@Component({
  components: {
    Container,
    Draggable
  }
})
export default class PurchaseOrderLineItemColumns extends Vue {
  @Prop({ type: Object, required: true }) store!: StoreModel;

  // State
  private loading = false;
  private saving = false;
  private hasChanges = false;

  // Column configs (stores order and enabled state)
  private columnConfigs: { [key: string]: ColumnConfig } = {};

  // Custom columns from API
  private customColumns: CustomColumnData[] = [];

  // Phase 3: Metafield columns
  private metafieldDefinitions: MetafieldDefinition[] = [];
  private metafieldColumns: { [key: string]: MetafieldColumnConfig } = {};
  private loadingMetafields = false;
  private metafieldError: string | null = null;

  // Custom column dialog state
  private loadingCustomColumns = false;
  private savingCustomColumn = false;
  private deletingCustomColumn = false;
  private customColumnDialog = false;
  private deleteDialog = false;
  private isEditingCustomColumn = false;
  private editingColumn: CustomColumnData | null = null;
  private deletingColumn: CustomColumnData | null = null;
  private customColumnFormValid = false;
  private customColumnFormData = {
    label: '',
    enabled: true
  };

  // Computed: unified list of all columns sorted by order
  get unifiedColumns(): UnifiedColumn[] {
    const allColumns: UnifiedColumn[] = [];

    // Add required columns
    REQUIRED_COLUMNS.forEach((col, index) => {
      const config = this.columnConfigs[col.id];
      allColumns.push({
        id: col.id,
        label: col.label,
        type: 'required',
        enabled: true, // Always enabled
        order: config?.order ?? (index + 1)
      });
    });

    // Add MasterData columns
    MASTERDATA_COLUMNS.forEach((col, index) => {
      const config = this.columnConfigs[col.id];
      allColumns.push({
        id: col.id,
        label: col.label,
        type: 'masterdata',
        enabled: config?.enabled ?? false,
        order: config?.order ?? (REQUIRED_COLUMNS.length + index + 1),
        category: col.category
      });
    });

    // Add custom columns
    this.customColumns.forEach(col => {
      allColumns.push({
        id: col.id,
        label: col.label,
        type: 'custom',
        enabled: col.enabled,
        order: col.order
      });
    });

    // Add metafield columns (Phase 3)
    this.metafieldDefinitions.forEach((def) => {
      const config = this.metafieldColumns[def.key];
      allColumns.push({
        id: `metafield_${def.key}`,
        label: config?.label || def.displayName,
        type: 'metafield',
        enabled: config?.enabled ?? false,
        order: config?.order ?? 200,
        metafieldKey: def.key,
        namespace: def.namespace
      });
    });

    // Sort by order
    return allColumns.sort((a, b) => a.order - b.order);
  }

  get selectedColumnsCount(): number {
    return this.unifiedColumns.filter(col => col.enabled).length;
  }

  get labelRules() {
    return [
      (v: string) => !!v || 'Label is required',
      (v: string) => (v && v.length <= 50) || 'Label must be 50 characters or less',
      (v: string) => this.isLabelUnique(v) || 'A column with this label already exists'
    ];
  }

  private isLabelUnique(label: string): boolean {
    if (!label) return true;
    const normalizedLabel = label.trim().toLowerCase();
    return !this.customColumns.some(col =>
      col.id !== this.editingColumn?.id &&
      col.label.toLowerCase() === normalizedLabel
    );
  }

  private getDefaultConfigs(): { [key: string]: ColumnConfig } {
    const defaults: { [key: string]: ColumnConfig } = {};

    // Columns that should be enabled by default
    const enabledByDefault = ['received_quantity', 'returned_quantity', 'units_left'];

    // Required columns: order 1-5 (after removing req_note)
    REQUIRED_COLUMNS.forEach((col, index) => {
      defaults[col.id] = {
        enabled: true,
        order: index + 1
      };
    });

    // MasterData columns: order 6+
    MASTERDATA_COLUMNS.forEach((col, index) => {
      defaults[col.id] = {
        enabled: enabledByDefault.includes(col.id),
        order: REQUIRED_COLUMNS.length + index + 1
      };
    });

    return defaults;
  }

  created() {
    this.columnConfigs = this.getDefaultConfigs();
  }

  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.fetchColumnConfig(),
        this.fetchCustomColumns(),
        this.fetchMetafieldDefinitions()
      ]);
    } finally {
      this.loading = false;
    }
  }

  async fetchColumnConfig() {
    try {
      const response: any = await get('/purchase_orders/config/line-item-columns');
      if (response.data && response.data.columns) {
        this.columnConfigs = { ...this.getDefaultConfigs(), ...response.data.columns };
      }
      // Load metafield columns config (Phase 3)
      this.metafieldColumns = response.data?.metafieldColumns || {};
    } catch (error) {
      console.error('Failed to load column configuration:', error);
      this.metafieldColumns = {};
    }
  }

  /**
   * Fetch available metafield definitions from backend (Phase 3)
   */
  async fetchMetafieldDefinitions() {
    this.loadingMetafields = true;
    this.metafieldError = null;

    try {
      const response: any = await get('/purchase_orders/config/metafield-definitions');
      this.metafieldDefinitions = response.data?.metafields || [];
    } catch (error: any) {
      console.error('Failed to fetch metafield definitions:', error);
      this.metafieldError = 'Failed to load metafield columns';
      this.metafieldDefinitions = [];
    } finally {
      this.loadingMetafields = false;
    }
  }

  async fetchCustomColumns() {
    this.loadingCustomColumns = true;
    try {
      const response: any = await get('/purchase_orders/config/custom-columns');
      const data = response.data;

      this.customColumns = Object.entries(data.columns || {}).map(([id, col]: [string, any]) => ({
        id,
        label: col.label,
        enabled: col.enabled,
        order: col.order
      })).sort((a, b) => a.order - b.order);
    } catch (error) {
      console.error('Failed to load custom columns:', error);
    } finally {
      this.loadingCustomColumns = false;
    }
  }

  onToggleMasterData(column: UnifiedColumn, enabled: boolean) {
    this.columnConfigs[column.id].enabled = enabled;
    this.hasChanges = true;
  }

  async onToggleCustom(column: UnifiedColumn, enabled: boolean) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    const previousValue = customCol.enabled;
    customCol.enabled = enabled;

    try {
      await put(`/purchase_orders/config/custom-columns/${column.id}`, { enabled });
    } catch (error) {
      // Revert on error
      customCol.enabled = previousValue;
      EventBus.$emit('show-snackbar', { text: 'Failed to update column', color: 'error' });
    }
  }

  /**
   * Toggle metafield column enabled state (Phase 3)
   */
  onToggleMetafield(column: UnifiedColumn, enabled: boolean) {
    if (!column.metafieldKey) return;

    const def = this.metafieldDefinitions.find(d => d.key === column.metafieldKey);
    if (!def) return;

    if (!this.metafieldColumns[column.metafieldKey]) {
      // Initialize config for newly enabled metafield
      // Preserve the column's current order instead of moving to bottom
      this.metafieldColumns[column.metafieldKey] = {
        enabled: enabled,
        order: column.order,
        label: def.displayName,
        namespace: def.namespace,
        name: def.name
      };
    } else {
      this.metafieldColumns[column.metafieldKey].enabled = enabled;
    }

    // Force reactivity update
    this.metafieldColumns = { ...this.metafieldColumns };
    this.hasChanges = true;
  }

  /**
   * Get next available order number
   */
  private getNextOrder(): number {
    const allOrders = this.unifiedColumns.map(c => c.order);
    return Math.max(...allOrders, 0) + 1;
  }

  /**
   * Get tooltip text for column category
   */
  private getCategoryTooltip(category: string): string {
    const tooltips: { [key: string]: string } = {
      'Basic': 'Basic product information from Shopify',
      'Pricing': 'Product pricing and cost data',
      'Quantities': 'Inventory quantity information',
      'Attributes': 'Product attributes like weight, HS code, etc.',
      'Metadata': 'Product tags, collections, and other metadata'
    };
    return tooltips[category] || `${category} column from Shopify product data`;
  }

  onDrop(dropResult: any) {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null || addedIndex === null || removedIndex === addedIndex) {
      return;
    }

    // Get current sorted list and reorder
    const sorted = [...this.unifiedColumns];
    const moved = sorted.splice(removedIndex, 1)[0];
    sorted.splice(addedIndex, 0, moved);

    // Update order values for all columns
    sorted.forEach((col, index) => {
      const newOrder = index + 1;

      if (col.type === 'required' || col.type === 'masterdata') {
        // Update local config
        if (!this.columnConfigs[col.id]) {
          this.columnConfigs[col.id] = { enabled: col.enabled, order: newOrder };
        } else {
          this.columnConfigs[col.id].order = newOrder;
        }
      } else if (col.type === 'custom') {
        // Update custom column order
        const customCol = this.customColumns.find(c => c.id === col.id);
        if (customCol) {
          customCol.order = newOrder;
        }
      } else if (col.type === 'metafield' && col.metafieldKey) {
        // Update metafield column order (Phase 3)
        if (this.metafieldColumns[col.metafieldKey]) {
          this.metafieldColumns[col.metafieldKey].order = newOrder;
        }
      }
    });

    // Force reactivity for metafield columns
    this.metafieldColumns = { ...this.metafieldColumns };
    this.hasChanges = true;
  }

  async save() {
    if (!this.hasChanges) return;

    this.saving = true;
    try {
      // Build metafieldColumns config for save (Phase 3)
      const metafieldColumnsToSave: { [key: string]: MetafieldColumnConfig } = {};
      this.metafieldDefinitions.forEach((def) => {
        const config = this.metafieldColumns[def.key];
        if (config) {
          const unified = this.unifiedColumns.find(u => u.metafieldKey === def.key);
          metafieldColumnsToSave[def.key] = {
            enabled: config.enabled,
            order: unified?.order ?? config.order,
            label: config.label || def.displayName,
            namespace: def.namespace,
            name: def.name
          };
        }
      });

      // Save MasterData, required column configs, and metafield columns
      await put('/purchase_orders/config/line-item-columns', {
        columns: this.columnConfigs,
        metafieldColumns: metafieldColumnsToSave
      });

      // Save custom column orders
      await Promise.all(
        this.customColumns.map(col =>
          put(`/purchase_orders/config/custom-columns/${col.id}`, { order: col.order })
        )
      );

      EventBus.$emit('show-snackbar', { text: 'Configuration saved successfully', color: 'success' });
      this.hasChanges = false;
    } catch (error) {
      console.error('Failed to save configuration:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to save configuration', color: 'error' });
    } finally {
      this.saving = false;
    }
  }

  // ========== Custom Column Dialog Methods ==========

  openCreateDialog() {
    this.isEditingCustomColumn = false;
    this.editingColumn = null;
    this.customColumnFormData = {
      label: '',
      enabled: true
    };
    this.customColumnDialog = true;
  }

  openEditDialog(column: UnifiedColumn) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    this.isEditingCustomColumn = true;
    this.editingColumn = customCol;
    this.customColumnFormData = {
      label: customCol.label,
      enabled: customCol.enabled
    };
    this.customColumnDialog = true;
  }

  goToCustomValues(column: UnifiedColumn) {
    this.$router.push({
      name: 'custom-column-values',
      query: { column: column.id }
    });
  }

  closeCustomColumnDialog() {
    this.customColumnDialog = false;
    this.editingColumn = null;
    (this.$refs.customColumnForm as any)?.reset?.();
  }

  async saveCustomColumn() {
    if (!this.customColumnFormValid) return;

    this.savingCustomColumn = true;
    const isCreating = !this.isEditingCustomColumn;
    const newColumnLabel = this.customColumnFormData.label;

    try {
      // Save pending order changes before creating new column to prevent losing them
      if (isCreating && this.hasChanges) {
        await this.save();
      }

      if (this.isEditingCustomColumn && this.editingColumn) {
        await put(
          `/purchase_orders/config/custom-columns/${this.editingColumn.id}`,
          this.customColumnFormData
        );
        EventBus.$emit('show-snackbar', { text: 'Column updated successfully', color: 'success' });
      } else {
        await post('/purchase_orders/config/custom-columns', this.customColumnFormData);
        EventBus.$emit('show-snackbar', { text: 'Column created successfully', color: 'success' });
      }

      this.closeCustomColumnDialog();
      await this.fetchCustomColumns();

      // Auto-scroll to newly created column
      if (isCreating) {
        this.$nextTick(() => {
          // Find the newly created column by label
          const newColumn = this.customColumns.find(c => c.label === newColumnLabel);
          if (newColumn) {
            const refName = `column-${newColumn.id}`;
            const element = this.$refs[refName] as HTMLElement[] | HTMLElement | undefined;
            const targetEl = Array.isArray(element) ? element[0] : element;
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add highlight effect
              targetEl.classList.add('highlight-new');
              setTimeout(() => targetEl.classList.remove('highlight-new'), 2000);
            }
          }
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save column';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.savingCustomColumn = false;
    }
  }

  confirmDelete(column: UnifiedColumn) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    this.deletingColumn = customCol;
    this.deleteDialog = true;
  }

  async deleteCustomColumn() {
    if (!this.deletingColumn) return;

    this.deletingCustomColumn = true;
    try {
      await del(`/purchase_orders/config/custom-columns/${this.deletingColumn.id}`);
      EventBus.$emit('show-snackbar', { text: 'Column deleted successfully', color: 'success' });
      this.deleteDialog = false;
      this.deletingColumn = null;
      await this.fetchCustomColumns();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete column';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.deletingCustomColumn = false;
    }
  }
}
